import { create } from 'zustand';

/**
 * Gates the desktop-chrome entrance moments that live INSIDE the boot
 * overlay's top-down reveal (see BootAnimation.tsx's 'revealing' stage +
 * .revealing/@keyframes topDownReveal in BootAnimation.module.scss). Read
 * directly by DesktopSystemBar, SpaceChat, and DesktopShell (rather than
 * prop-drilled through the desktop layout components) — same pattern as
 * useDesktopChatStore's intake flags being read deep inside DesktopShell.
 *
 * `stage` mirrors where the reveal-mask currently is:
 *   - 'idle'    — no boot sequence in progress (the default, and where this
 *     always ends up again once the sequence below finishes). Non-boot
 *     onboarding intake (no signup boot animation involved) never sees
 *     anything other than 'idle', so it's unaffected by any of this.
 *   - 'pending' — a boot sequence IS in progress but hasn't reached the bar
 *     yet (set the instant BootAnimation enters its 'revealing' stage, i.e.
 *     before the mask has moved at all). Every gated piece of chrome must
 *     treat 'pending' the same as 'bar' (stay hidden) — see the two-part
 *     reveal note below.
 *   - 'bar'     — the mask has just uncovered the system bar's height and is
 *     holding there; DesktopSystemBar fades its content in during this window.
 *   - 'chat'    — the mask has fully retracted (whole desktop uncovered) AND
 *     a short `postRevealPauseMs` beat has passed (see BootAnimation.tsx's
 *     advanceToChat / bootScene.ts) so the vertical reveal reads as its own
 *     finished step; the window surface, Open-Windows sidebar, dock
 *     (DesktopShell.tsx / .module.scss), and SpaceChat (SpaceChat.tsx / .scss)
 *     all play their slide-in starting here.
 *
 * IMPORTANT: every one of those pieces of chrome can render (and would
 * otherwise sit there immediately, fully visible) well before the mask has
 * revealed anything — e.g. SpaceChat's onboarding-intake variant mounts the
 * instant `proceedPastIntro()` runs, the same moment 'pending' is set. So
 * "stage is 'idle' or 'chat'" is NOT the same test as "this chrome should be
 * visible" — each must stay hidden for BOTH 'pending' and 'bar' (i.e. hidden
 * whenever stage is anything other than 'idle' or 'chat'), not just
 * default-visible until told otherwise.
 *
 * Entirely driven by BootAnimation.tsx's own timers, which is the single
 * owner of this whole sequence's timing since it owns the CSS animation
 * these timestamps are keyed off of.
 */
export type BootChoreographyStage = 'idle' | 'pending' | 'bar' | 'chat';

interface BootChoreographyState {
  stage: BootChoreographyStage;
  setStage: (stage: BootChoreographyStage) => void;
}

export const useBootChoreographyStore = create<BootChoreographyState>((set) => ({
  stage: 'idle',
  setStage: (stage) => set({ stage }),
}));
